/* eslint-disable max-lines */

import { ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';

import { getBexApiContext } from 'bernie-context';

import { adaptSearchCriteria } from 'stores/adapters/bex-api/common/search-criteria-adapter';
import { ChildModel } from 'stores/models/child-model';
import { evaluateAndLogExperiment } from 'stores/utils';
import { getShoppingContextInput } from 'stores/adapters/bex-api/search/shopping-context-adapter';
import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { UrlParamQueryIdMapper } from 'stores/models';

import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';

import {
  BooleanValueInput,
  ChildInput,
  CoordinatesInput,
  DestinationInput,
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchSortAndFilter,
  PaginationInput,
  PropertyDateRangeInput,
  RoomInput,
} from '__generated__/api';

const DEFAULT_STAY_LENGTH = '1';

export const getPropertySearchInput = (
  context: ContextStore,
  searchCriteria: SearchCriteriaModel,
  multiItemContext?: MultiItemContext,
  adsOnly: boolean = false,
  searchPagination?: PaginationInput,
  experiments?: ExperimentStore,
  pageId?: string,
  returnPropertyType?: boolean
): LodgingPwaPropertySearch.Variables => {
  const pageName = pageId?.split(',')[0];

  return {
    context: getBexApiContext(context),
    criteria: adaptSearchCriteria(context, searchCriteria, adsOnly, searchPagination),
    destination: {
      regionName: null,
      regionId: null,
      coordinates: null,
      pinnedPropertyId: null,
      propertyIds: null,
      mapBounds: null,
    },
    shoppingContext: getShoppingContextInput(multiItemContext),
    shouldFetchSortAndFilter: getShouldFetchSortAndFilter(context, experiments, pageName),
    returnPropertyType,
  };
};

export const getPropertySortAndFilterInputs = (
  context: ContextStore,
  searchCriteria: SearchCriteriaModel,
  multiItemContext?: MultiItemContext,
  adsOnly: boolean = false,
  searchPagination?: PaginationInput
): LodgingPwaPropertySearchSortAndFilter.Variables => {
  const variables: LodgingPwaPropertySearchSortAndFilter.Variables = {
    context: getBexApiContext(context),
    criteria: adaptSearchCriteria(context, searchCriteria, adsOnly, searchPagination),
    destination: {
      regionName: null,
      regionId: null,
      coordinates: null,
      pinnedPropertyId: null,
      propertyIds: null,
      mapBounds: null,
    },
    shoppingContext: getShoppingContextInput(multiItemContext),
  };

  return variables;
};

/**
 * Returns the BooleanValueInput[] necessary for the secondary.booleans on the searchCriteria/criteria query variable.
 * But before that it will filter out any undefined or false values from the list.
 * @param searchCriteria - list of available search criteria URL parameters
 * @param paramQueryIds - object list of all the parameter names that we want to add to the boolean array
 * @returns BooleanValueInput[] - contains as an array element the id as the parameter name and the value as the parameter value
 */
export const getSearchCriteriaSecondaryBooleans = (
  searchCriteria: SearchCriteriaModel,
  paramQueryIds: UrlParamQueryIdMapper[]
): BooleanValueInput[] | null => {
  if (paramQueryIds?.length > 0) {
    return paramQueryIds
      .filter(id => searchCriteria[id?.URL_PARAM_ID] === 'true')
      .map(id => ({
        id: id?.QUERY_BOOLEAN_ID,
        value: true,
      }));
  }

  return [];
};

export const getRooms = (searchCriteria: SearchCriteriaModel): RoomInput[] | null => {
  if (searchCriteria.rooms) {
    return searchCriteria.rooms.map(getRoomInput);
  }

  return null;
};

const generatePropertyDateRangeInput = (checkInDate: string | undefined, checkOutDate: string | undefined): PropertyDateRangeInput => ({
  checkInDate: DateUtils.parseDateString(checkInDate),
  checkOutDate: DateUtils.parseDateString(checkOutDate),
});

export const getDateRangeFromSearchCriteria = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput => {
  const { startDate, endDate, partialStay, partialStayStartDate, partialStayEndDate } = searchCriteria;
  if (partialStay === 'true') {
    return generatePropertyDateRangeInput(partialStayStartDate, partialStayEndDate);
  }

  return generatePropertyDateRangeInput(startDate, endDate);
};

export const getDateRangeInput = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  const { checkInDate, checkOutDate } = getDateRangeFromSearchCriteria(searchCriteria);
  if (!checkInDate || !checkOutDate) {
    return generateFutureDates(searchCriteria);
  }

  return {
    checkInDate,
    checkOutDate,
  };
};

export const getDestinationInput = (searchCriteria: SearchCriteriaModel): DestinationInput => {
  const coordinates: CoordinatesInput | null = getCoordinates(searchCriteria.latLong);

  const mapBounds: CoordinatesInput[] =
    !searchCriteria.mapBounds || searchCriteria.mapBounds.length !== 2
      ? null
      : [getCoordinates(searchCriteria.mapBounds[0]), getCoordinates(searchCriteria.mapBounds[1])];

  return {
    regionName: searchCriteria.destination || null,
    regionId: getRegionId(searchCriteria.regionId),
    coordinates,
    pinnedPropertyId: searchCriteria.selected || null,
    propertyIds: getPropertyIds(searchCriteria),
    mapBounds,
  };
};

const getRegionId = (regionId: string): string | null => {
  if (!regionId) {
    return null;
  }
  const parsedRegionId = Number.parseInt(regionId, 10);
  if (isNaN(parsedRegionId)) {
    return null;
  }

  return regionId;
};

const getCoordinates = (latLong?: string): CoordinatesInput | null => {
  if (!latLong) {
    return null;
  }

  const latLongParts = latLong.toString().split(',') || [];

  if (!latLongParts || latLongParts.length !== 2 || !latLongParts[0]) {
    return null;
  }

  const parsedLat = Number.parseFloat(latLongParts[0]);
  const parsedLong = Number.parseFloat(latLongParts[1]);

  if (isNaN(parsedLat) || isNaN(parsedLong)) {
    return null;
  }

  return {
    latitude: Number.parseFloat(latLongParts[0]),
    longitude: Number.parseFloat(latLongParts[1]),
  };
};

const getRoomInput = (roomModel: RoomModel): RoomInput => ({
  adults: roomModel.adults,
  children: roomModel.children.map(getChildInput),
});

const getChildInput = (childModel: ChildModel): ChildInput => ({
  age: childModel.age,
});

const getPropertyIds = (searchCriteria: SearchCriteriaModel): string[] | null => {
  if (searchCriteria.group && searchCriteria.group.length > 0) {
    return searchCriteria.group.split(',');
  }

  return null;
};

const generateFutureDates = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  if (!searchCriteria?.daysInFuture) {
    return null;
  }
  const stayLength = searchCriteria.stayLength ? searchCriteria.stayLength : DEFAULT_STAY_LENGTH;
  const checkInDate = new Date();
  checkInDate.setDate(checkInDate.getDate() + parseInt(searchCriteria.daysInFuture, 10));
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkInDate.getDate() + parseInt(stayLength, 10));

  return {
    checkInDate: DateUtils.parseDateString(checkInDate.toISOString().split('T')[0]),
    checkOutDate: DateUtils.parseDateString(checkOutDate.toISOString().split('T')[0]),
  };
};

const getShouldFetchSortAndFilter = (context: ContextStore, experiments?: ExperimentStore, pageName?: string) =>
  evaluateAndLogExperiment('LShop_SRP_Delayed_Sort_And_Filter', context, experiments, { pageName } as PageStore) !== 1;
